// WorkspaceAvatar.tsx — initial-letter workspace avatar
// (workspace-first shell spec, 2026-08-21: "Workspace avatar is an initial, not an uploaded
// logo"). Colour is a stable hash of the id over 6 existing palette-token pairings, so the same
// workspace always looks the same and — across 6 swatches — repeats are uncommon but not
// impossible for a user with several workspaces.
//
// Fix round 1 (review, 2026-08-21): the original 8-entry table paired `--chart-2..5` (aliases of
// `--st-crawling/indexing/paused/error`) with hardcoded `text-white` / `text-foreground`. Those
// status tokens go LIGHT under `.dark` (e.g. `--st-paused: oklch(0.78 0.1 75)`), so a near-white
// foreground on a near-white background measured 1.78-2.65:1 in dark mode -- well under WCAG's
// 4.5:1 floor. Re-paired every chart-x swatch with `text-background` instead: `--background` sits
// at the opposite end of the lightness scale from every chart-x colour in BOTH themes (dark in
// light mode, light in dark mode), which is exactly the property "always legible" needs, and it's
// an existing token, not a new one. Verified by computing WCAG contrast directly from each
// token's oklch values (throwaway script, not committed) -- every retained pair clears 4.5:1 in
// both themes, the tightest being chart-4/background at 4.75:1 in light:
//   chart-1 (primary)      / primary-foreground : light 5.31:1  dark 8.45:1
//   chart-2 (st-crawling)  / background          : light 5.66:1  dark 8.44:1
//   chart-3 (st-indexing)  / background          : light 6.06:1  dark 8.07:1
//   chart-4 (st-paused)    / background          : light 4.75:1  dark 9.51:1
//   chart-5 (st-error)     / background          : light 5.74:1  dark 7.28:1
//   accent                 / accent-foreground   : light 13.63:1 dark 12.27:1
//
// `--secondary` and `--muted` are literally identical oklch triples in both themes (955/6/80 vs
// 955/6/80 light, 265/8/75 vs 265/8/75 dark) and `--accent` is within 0.01 lightness of both --
// three entries that would only ever render as one or two visually distinct colours. Collapsed to
// the single `accent`/`accent-foreground` pairing below; 8 nominal entries were never 8 visually
// distinct ones.
const SWATCHES = [
  'bg-[var(--chart-1)] text-primary-foreground',
  'bg-[var(--chart-2)] text-background',
  'bg-[var(--chart-3)] text-background',
  'bg-[var(--chart-4)] text-background',
  'bg-[var(--chart-5)] text-background',
  'bg-[var(--accent)] text-accent-foreground',
] as const;

function swatchFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return SWATCHES[h % SWATCHES.length]!;
}

function initialOf(name: string): string {
  const first = [...name.trim()][0];
  return (first ?? '?').toUpperCase();
}

export function WorkspaceAvatar({
  name,
  id,
  size = 24,
  logoUrl,
}: {
  name: string;
  id: string;
  size?: number;
  /** `null`/undefined renders the initial-letter avatar; a URL renders the uploaded logo
   * instead (workspace-logo cycle, 2026-08-21 — spec §A). Same ring container either way so
   * swapping between the two never shifts layout. */
  logoUrl?: string | null;
}) {
  return (
    // Hairline inset ring: the `--accent` swatch is within 1.2:1 of the rail surface and of the
    // trigger's `hover:bg-accent`, so without an edge the square dissolves into a floating letter
    // on hover (review, 2026-08-21). `foreground/10` reads as an edge on every swatch, both themes.
    <span
      aria-hidden="true"
      className={`grid shrink-0 place-items-center overflow-hidden rounded-md font-semibold leading-none ring-1 ring-inset ring-foreground/10 ${logoUrl ? '' : swatchFor(id)}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.5) }}
    >
      {logoUrl ? (
        <img src={logoUrl} alt="" className="size-full rounded-md object-cover" />
      ) : (
        initialOf(name)
      )}
    </span>
  );
}
